import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Popover } from '@mui/material'
import { splitOnMatch } from '@common/helpers/noteAttribution/highlightMatch'
import { ReportingPeriods } from '@common/interfaces/bbc'
import { NoteCellLinkConfidenceBasis } from '@common/interfaces/noteCellLink'
import { getColumnIdsForCellNotesList } from '@common/interfaces/notes'
import cn from 'classnames'
import { useCellNoteLinks } from '../../../../hooks/useCellNoteLinks'
import { ICellNoteLinkRow } from '../../../../redux/cellNotes/reducer'
import Text from '../../Text'
import styles from './CellContextPanel.module.scss'

/** Confidence rendered as filled bars out of three, strongest first. */
const CONFIDENCE_STRENGTH: Record<NoteCellLinkConfidenceBasis, number> = {
  [NoteCellLinkConfidenceBasis.Exact]: 3,
  [NoteCellLinkConfidenceBasis.Synonym]: 2,
  [NoteCellLinkConfidenceBasis.Inferred]: 1,
}

const CONFIDENCE_LABEL: Record<NoteCellLinkConfidenceBasis, string> = {
  [NoteCellLinkConfidenceBasis.Exact]: 'Named this line directly',
  [NoteCellLinkConfidenceBasis.Synonym]: 'Matched a known wording for this line',
  [NoteCellLinkConfidenceBasis.Inferred]: 'Inferred from the surrounding text',
}

const BAR_SLOTS = [1, 2, 3]

/** The header the words are leaving, named when the link still has its note behind it. */
const originOf = (link: ICellNoteLinkRow): string =>
  link.sourceColumnId ? `from the ${link.sourceColumnId} column header` : 'from column header'

/**
 * The cell the words are going to — the link's own coordinates, which are not always the
 * cell this panel is anchored to. Approving writes there, so that is what has to be named.
 */
const targetOf = (link: ICellNoteLinkRow): string =>
  [link.rowId, link.columnId].filter(Boolean).join(' · ') || 'this cell'

interface IProps {
  tableType: string
  clientId: string
  workflow: string
  id: string
  rowId: string
  columnId: string
  /** Drives the roll-up: a quarterly cell has to ask for its months too. */
  periodView?: ReportingPeriods
  recordDate?: string
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
}

/**
 * The cell's review panel: proposals waiting on a decision, and nothing else.
 *
 * It used to carry everything attributed to the cell — established facts and accepted notes
 * alongside the pending ones — which split "what is on this cell" across two surfaces and let
 * the marker's count disagree with whichever one you opened. Reading belongs entirely to the
 * notes drawer now; this panel exists to approve or dismiss.
 *
 * Anchored to the marker rather than shown as a modal, since it is cell context, not a task.
 * Details load when it opens; the grid's markers come from the table-level indicator call, so
 * no request is made per cell.
 */
const CellContextPanel = ({
  tableType,
  clientId,
  workflow,
  id,
  rowId,
  columnId,
  periodView,
  recordDate,
  anchorEl,
  open,
  onClose,
}: IProps) => {
  // '' is table scope and must stay queryable, but the helper returns [] for it.
  const columnIds = useMemo(
    () => (columnId ? getColumnIdsForCellNotesList(columnId, periodView) : ['']),
    [columnId, periodView],
  )

  const { suggested, isLoading, confirm, dismiss } = useCellNoteLinks({
    tableType,
    clientId,
    workflow,
    id,
    rowId,
    columnId,
    columnIds,
    recordDate,
    enabled: open,
  })

  // Closes itself once the last suggestion has been decided, rather than sitting open on
  // "Nothing waiting for review." Only after a decision, never on open: a cell opened with
  // nothing pending still gets to say so, and a still-loading panel has not decided anything.
  const hadSuggestionsRef = useRef(false)
  useEffect(() => {
    if (!open || isLoading) return
    if (suggested.length > 0) {
      hadSuggestionsRef.current = true
      return
    }
    if (hadSuggestionsRef.current) {
      hadSuggestionsRef.current = false
      onClose()
    }
  }, [open, isLoading, suggested.length, onClose])

  const cellReference = useMemo(
    () => [rowId, columnId].filter(Boolean).join(' · ') || 'This table',
    [rowId, columnId],
  )

  const handleApprove = useCallback((linkId: string) => confirm(linkId), [confirm])
  const handleDismiss = useCallback((linkId: string) => dismiss(linkId), [dismiss])

  const renderConfidence = (basis: NoteCellLinkConfidenceBasis | null) => {
    if (!basis) return null
    const strength = CONFIDENCE_STRENGTH[basis] ?? 1

    return (
      <span className={styles.confidence} title={CONFIDENCE_LABEL[basis]}>
        {BAR_SLOTS.map((slot) => (
          <span key={slot} className={cn(styles.bar, { [styles.barFilled]: slot <= strength })} />
        ))}
      </span>
    )
  }

  const renderSuggested = (link: ICellNoteLinkRow) => {
    // The slice of the note that belongs to this cell, once segmentation has run; the whole
    // note until then. matchOffsets are offsets into the WHOLE note, so the highlight only
    // applies when that is what is being shown — against a slice they would land anywhere.
    const { before, match, after } = link.segmentText
      ? { before: link.segmentText, match: '', after: '' }
      : splitOnMatch(link.displayText, link.matchOffsets)

    return (
      <li key={link.id} className={styles.row}>
        {/*
          Spelled out as an action rather than left as bare note text: the row is a proposal,
          and "Trade discounts are red" on its own reads as a statement about this cell rather
          than as something waiting to be moved onto it.

          Both ends are named rather than left as "this cell", because neither is reliably the
          cell you are looking at. A quarterly cell rolls up its months, so a suggestion
          recorded on Nov 2022 is offered from the Q4 2022 cell — and approving it writes the
          note on Nov 2022, not on the quarter. Saying "this cell" there named the wrong
          target. The origin is spelled out for the same reason: a note can be pinned to one
          period's header while naming another in its text.
        */}
        <Text variant="bodySm" tone="primary" casing="none" className={styles.text}>
          Move ‘{before}
          {match && <mark className={styles.match}>{match}</mark>}
          {after}’ {originOf(link)} to {targetOf(link)}
        </Text>
        <div className={styles.rowFooter}>
          {renderConfidence(link.confidenceBasis)}
          <div className={styles.actions}>
            <button
              type="button"
              className={cn(styles.action, styles.approve)}
              onClick={() => handleApprove(link.id)}
            >
              <Text variant="button">Approve</Text>
            </button>
            <button
              type="button"
              className={cn(styles.action, styles.dismiss)}
              onClick={() => handleDismiss(link.id)}
            >
              <Text variant="button">Dismiss</Text>
            </button>
          </div>
        </div>
      </li>
    )
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { className: styles.paper } }}
    >
      <div className={styles.root} data-cy="cell-context-panel">
        <header className={styles.header}>
          <span className={styles.reference}>{cellReference}</span>
        </header>

        {isLoading ? (
          <Text variant="bodySm" tone="tertiary" className={styles.loading}>
            Loading…
          </Text>
        ) : suggested.length === 0 ? (
          <Text variant="bodySm" tone="tertiary" className={styles.empty}>
            Nothing waiting for review.
          </Text>
        ) : (
          <section className={styles.section}>
            <Text variant="eyebrow" tone="tertiary" component="h4" className={styles.sectionTitle}>
              Suggested
            </Text>
            <ul className={styles.list}>{suggested.map(renderSuggested)}</ul>
          </section>
        )}
      </div>
    </Popover>
  )
}

export default CellContextPanel
